/**
 * apps 域 Zod 共享契约 — ADM-APP-S001~S011
 *
 * 字段映射详见 dev-docs `03-数据字典/70-产品配置.md` §1 + §1.1 + §1.1.1
 */

import { z } from 'zod';
import { NovelPurchaseProductMappingSchema } from './novel-length-price';
import type { NovelPurchaseProductMapping } from './novel-length-price';
import type { SettlementChannel } from './payment-methods';
import { type RewardedAdConfig, RewardedAdConfigSchema } from './rewarded-ads';
import { LanguageCodeSchema } from './system-config';

/** 产品 App 平台；系统哨兵行不会通过此契约暴露。 */
export const AppPlatformSchema = z.enum(['IOS', 'ANDROID', 'WEB']);
export type AppPlatform = z.infer<typeof AppPlatformSchema>;

/** GET /apps 查询参数。 */
export const ListAppsQuerySchema = z.object({
  platform: AppPlatformSchema.optional(),
  is_active: z
    .enum(['true', 'false'])
    .optional()
    .transform((value) => (value === undefined ? undefined : value === 'true')),
  q: z.string().trim().min(1).max(100).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(20),
});
export type ListAppsQuery = z.infer<typeof ListAppsQuerySchema>;

/**
 * App 运营市场（地区）集合（ISO 3166-1 alpha-2 大写），multi-select；替代旧 country 单值字段
 * （dev-docs PR #63 ADM-APP-S004 / ADM-SYS-S001）。校验：非空 + 全为 ^[A-Z]{2}$（大写）+ distinct。
 * 候选源 system_regions 由 service 层校验（PG CHECK 不能跨表）。
 * ❌ 禁止用 region_codes[0] 推导主市场：无主次集合，数组位置无稳定业务语义。
 */
const RegionCodesSchema = z
  .array(z.string().regex(/^[A-Z]{2}$/, 'Must be ISO 3166-1 alpha-2 (upper-case)'))
  .min(1)
  .refine((arr) => new Set(arr).size === arr.length, 'Region codes must be distinct');
/**
 * App 允许语言集合（BCP 47 受限子集），替代旧 locale 单值字段（dev-docs PR #60 ADM-APP-S005）。
 * 校验：非空 + LanguageCodeSchema + distinct；`zh` 及 `zh-*` 禁入（仅 admin 内部用）。
 * `pt` 与 `pt-BR` 是两个独立成员。dev-docs #413：'en' 转普通候选，不再必含。
 */
const AllowedLanguagesSchema = z
  .array(LanguageCodeSchema)
  .min(1)
  .refine((arr) => new Set(arr).size === arr.length, 'Languages must be distinct')
  .refine(
    (arr) => !arr.some((c) => c === 'zh' || c.startsWith('zh-')),
    "'zh' and zh-* not allowed in allowed_languages",
  );
/**
 * App 默认内容语言（ADM-APP-S012）；取值须 ∈ 本行 allowed_languages。
 * 格式同 LanguageCodeSchema；zh / zh-* 经 allowed_languages 成员关系间接禁入。
 */
const DefaultLanguageSchema = LanguageCodeSchema;
/** Slug — URL friendly */
const SlugSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Slug must be lowercase letters / digits / hyphens');

/**
 * 包体代号（ADM-APP-S019，`03-数据字典/70-产品配置.md` §1）：大写字母与数字 2–12 位。
 *
 * **不做大小写归一 / trim**：契约写的是「不合 `^[A-Z0-9]{2,12}$` → `VALIDATION_FAILED`」，服务端
 * 静默改写会让运营提交的值与落库值不一致、审计里也对不上；输入框侧的大写化交给前端。
 * 判据与 C 端 CHECK `apps_app_code_shape` 同源，全局唯一由 DB `apps_app_code_partial_unique` 承担。
 */
export const AppCodeSchema = z
  .string()
  .regex(/^[A-Z0-9]{2,12}$/, 'app_code must be 2-12 upper-case letters or digits');

/**
 * App Link Schema —— 原生 App 的 deep link scheme，WEB 不使用。
 * 契约源 dev-docs `03-数据字典/70-产品配置.md` §1（原生非空，WEB NULL）+ `04-业务流程/50-推送与归因.md` §6。
 * URI-scheme 卫生正则（RFC 3986 scheme 子集，小写起首字母）：防 `://` / 空格 / 斜杠等破坏 deep link `<scheme>://...` 拼接的字符。
 */
const AppLinkSchemeSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(
    /^[a-z][a-z0-9+.-]*$/,
    'Must be a valid URI scheme (lowercase, e.g. novellea / novellea-dev)',
  );

/** WEB 主屏触点的完整配置；PATCH 整份替换，不在共享输入上施加默认值。 */
export const HomeScreenConfigSchema = z
  .object({
    is_enabled: z.boolean(),
    is_reader_banner_enabled: z.boolean(),
    is_payment_success_dialog_enabled: z.boolean(),
    chapter_threshold: z.number().int().positive(),
    reader_minutes_threshold: z.number().int().positive(),
    cooldown_hours: z.number().int().positive(),
  })
  .strict();
export type HomeScreenConfig = z.infer<typeof HomeScreenConfigSchema>;
export const DEFAULT_HOME_SCREEN_CONFIG: Readonly<HomeScreenConfig> = {
  is_enabled: true,
  is_reader_banner_enabled: true,
  is_payment_success_dialog_enabled: true,
  chapter_threshold: 5,
  reader_minutes_threshold: 10,
  cooldown_hours: 48,
};

/** 平台相关标识：原生 App 必须有 scheme；WEB 不登记原生商店和第三方 SDK 身份。 */
export const AppPlatformConfigSchema = z
  .object({
    platform: AppPlatformSchema,
    home_screen_config: HomeScreenConfigSchema.nullable().optional(),
    app_link_scheme: AppLinkSchemeSchema.nullable().optional(),
    bundle_id_ios: z.string().max(255).nullable().optional(),
    package_name_android: z.string().max(255).nullable().optional(),
    adapty_app_id: z.string().max(255).nullable().optional(),
    appsflyer_property_id: z.string().max(255).nullable().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.platform === 'WEB') {
      if (value.home_screen_config === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['home_screen_config'],
          message: 'home_screen_config must be a complete object for WEB',
        });
      }
      for (const field of [
        'app_link_scheme',
        'bundle_id_ios',
        'package_name_android',
        'adapty_app_id',
        'appsflyer_property_id',
      ] as const) {
        if (value[field] !== undefined && value[field] !== null) {
          ctx.addIssue({ code: 'custom', path: [field], message: `${field} must be null for WEB` });
        }
      }
    } else {
      if (value.home_screen_config != null) {
        ctx.addIssue({
          code: 'custom',
          path: ['home_screen_config'],
          message: 'home_screen_config must be null for native apps',
        });
      }
      if (!value.app_link_scheme) {
        ctx.addIssue({
          code: 'custom',
          path: ['app_link_scheme'],
          message: 'app_link_scheme is required for native apps',
        });
      }
    }
  });

/**
 * App 层千字单价（`apps.default_chapter_price_per_kw`，minor units / coin 的 bigint 串）。
 *
 * **下限 `>= 1`**（dd#999：`02-API契约/AB-运营应用.md` §3 / §4 与 `03-数据字典/70-产品配置.md`
 * 逐处写明「越界 → `VALIDATION_FAILED`」）——配成 `0` 是事故而非免费玩法：整书免费走
 * `novels.limited_free_enabled` 或 `free_chapter_count` 覆盖全书（`03-数据字典/20-内容.md` §1.6），
 * 那两种情况下单价照常、每章照常持有派生价、只是不收取。与 novel 层
 * `novels.cost_default_per_kw` 同一条判据（`packages/shared/src/schemas/books.ts`）。
 *
 * 本 schema **只服务这一个字段**，故直接收下限；新增别的金额串字段时另起，不要复用本条。
 */
export const ChapterPricePerKwSchema = z
  .string()
  .regex(/^[1-9][0-9]*$/, 'Must be a positive integer string (>= 1)');

/** Bonus ratio — decimal(5,4)，[0, 9.9999] */
const BonusRatioSchema = z
  .string()
  .regex(/^[0-9]+(\.[0-9]{1,4})?$/, 'Must be decimal with at most 4 fraction digits');

/** 任务奖励 contract（dev-docs §1.1.1） */
const ReadingTimeTierSchema = z.object({
  minutes: z.number().int().positive(),
  reward: z.number().int().nonnegative(),
});

const UnlockChapterTierSchema = z.object({
  chapters: z.number().int().positive(),
  reward: z.number().int().nonnegative(),
});

export const TaskCenterRewardsSchema = z
  .object({
    fixed: z
      .object({
        app_review: z.number().int().nonnegative(),
        enable_notification: z.number().int().nonnegative(),
        account_login: z.number().int().nonnegative(),
        bind_email: z.number().int().nonnegative(),
        daily_first_recharge: z.number().int().nonnegative(),
      })
      .strict(),
    tiered: z
      .object({
        daily_signin: z.array(z.number().int().nonnegative()).length(7, '7 days required'),
        reading_time: z
          .array(ReadingTimeTierSchema)
          .min(1)
          .refine(
            (arr) => arr.every((v, i) => i === 0 || v.minutes > (arr[i - 1]?.minutes ?? 0)),
            'reading_time minutes must be strictly ascending',
          ),
        unlock_chapter: z
          .array(UnlockChapterTierSchema)
          .min(1)
          .refine(
            (arr) => arr.every((v, i) => i === 0 || v.chapters > (arr[i - 1]?.chapters ?? 0)),
            'unlock_chapter chapters must be strictly ascending',
          ),
      })
      .strict(),
  })
  .strict();

export type TaskCenterRewards = z.infer<typeof TaskCenterRewardsSchema>;

/**
 * 进入 `/me/tasks` 的 8 个普通任务 slug（ADM-APP-S016）。
 *
 * **顺序 = 契约 `03-数据字典/70-产品配置.md` §1.2 默认值逐字**（应用评分 → 开启通知 → 账号登录 →
 * 绑定邮箱 → 每日首充 → 阅读时长 → 付费章节解锁 → 广告任务），也是 `DEFAULT_TASK_CENTER_TASK_DISPLAY` 的顺序。
 *
 * 🔒 **必须与 C 端 `novel-app-server` `src/lib/task-center-task-display.ts#TASK_CENTER_DISPLAY_SLUGS`
 * 逐字一致**（同一条 §1.2 key 全集、两仓各一份）。改这里之前先
 * `git -C ../novel-app-server show origin/main:src/lib/task-center-task-display.ts` 对一遍 ——
 * 两边不一致时，admin 收下的配置 C 端会判成违规并**按关闭处理**，运营在后台看到开着、用户看不到。
 *
 * **`daily_signin` 刻意不在集合内**：它走 `/me/check-in` 独立端点、不进 `/me/tasks`（§1.2 注），
 * 写入本字段即 `VALIDATION_FAILED`。订阅奖励（`/me/subscription/grants`）同理不由本字段承载。
 */
export const TASK_CENTER_DISPLAY_SLUGS = [
  'app_review',
  'enable_notification',
  'account_login',
  'bind_email',
  'daily_first_recharge',
  'reading_time',
  'unlock_chapter',
  'watch_rewarded_ad',
] as const;

export type TaskCenterDisplaySlug = (typeof TASK_CENTER_DISPLAY_SLUGS)[number];

export const TaskCenterDisplaySlugSchema = z.enum(TASK_CENTER_DISPLAY_SLUGS);

/** slug 全集，用于「元素形状坏了但 slug 合法」时判定它不算缺失 */
const SLUG_SET: ReadonlySet<string> = new Set(TASK_CENTER_DISPLAY_SLUGS);

/**
 * 单个配置项。**`.strict()` 不可省** —— 契约 §1.2.1「元素 shape」写的是「必须**且只含**
 * `task_slug` + `is_enabled`」。放任多余键会让「配置存储层不设排序整数」这条被绕过
 * （运营前端塞个 `display_order` 进来、后端照收，两处排序源从此分叉）。
 */
export const TaskCenterTaskDisplayEntrySchema = z
  .object({
    task_slug: TaskCenterDisplaySlugSchema,
    is_enabled: z.boolean(),
  })
  .strict();

export type TaskCenterTaskDisplayEntry = z.infer<typeof TaskCenterTaskDisplayEntrySchema>;

/**
 * `apps.task_center_task_display` 的写入校验（契约 §1.2.1 四条硬校验逐条落地）。
 *
 * | 契约校验项 | 这里怎么实现 |
 * |---|---|
 * | 覆盖全集（缺任一 → 失败，`details` 指明缺哪个）| `superRefine` 逐 slug 比对，缺项各出一条 issue 并带 `params.missing_slug` |
 * | 禁止 extras（含 `daily_signin`）| 元素 schema 的 `z.enum` |
 * | 禁止重复 | `superRefine` 记 seen，重复项在**该元素下标**上报 |
 * | 元素 shape 只含两键 | 元素 schema 的 `.strict()` |
 *
 * ⚠️ **入参声明成 `z.array(z.unknown())`、元素校验在 `superRefine` 内手工跑**，不是
 * `z.array(元素schema).superRefine(...)`：后者在**任一元素失败时就中止**，外层的 refine 根本不执行 ——
 * 于是「用 `daily_signin` 顶替 `app_review`」这类请求只报 extras、**一条缺失都不报**，
 * 契约要的 details 全没了（codex r5 抓的）。手工跑元素校验才能让「形状错」与「缺哪些」同时出。
 *
 * **顺序不校验**：契约明写「数组元素可任意重排」，`task_center_rewards.tiered` 的禁止重排约束
 * **不适用于本字段** —— 顺序本身就是运营要配的东西。
 *
 * ⚠️ **不挂 `.default()`**：本文件已有两处踩过同一个坑（Zod 4 的 `.partial()` 不抵消 `.default()`，
 * 默认值会漏进 PATCH，把「没传就不改」变成「没传就重置」）。创建侧的缺省由 service 显式写
 * `DEFAULT_TASK_CENTER_TASK_DISPLAY`，不靠 schema 兜。
 */
export const TaskCenterTaskDisplaySchema = z
  .array(z.unknown())
  .superRefine((raw, ctx) => {
    const seen = new Set<string>();
    raw.forEach((element, index) => {
      const parsed = TaskCenterTaskDisplayEntrySchema.safeParse(element);
      // slug 单独取：元素**整体**不合法（如 is_enabled 非 boolean）时也要认出它占了哪个 slug，
      // 否则那个 slug 会被同时报成「形状错」和「缺失」，运营看到两条互相矛盾的原因
      const rawSlug = (element as { task_slug?: unknown } | null)?.task_slug;
      const slug = typeof rawSlug === 'string' ? rawSlug : undefined;

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          ctx.addIssue({ ...issue, path: [index, ...issue.path] });
        }
        // 闭集内的 slug 即使元素形状坏了也记为「出现过」——它不算缺失
        if (slug !== undefined && SLUG_SET.has(slug)) seen.add(slug);
        return;
      }
      if (seen.has(parsed.data.task_slug)) {
        ctx.addIssue({
          code: 'custom',
          path: [index, 'task_slug'],
          message: `duplicate task_slug: ${parsed.data.task_slug}`,
          params: { duplicate_slug: parsed.data.task_slug },
        });
        return;
      }
      seen.add(parsed.data.task_slug);
    });

    for (const slug of TASK_CENTER_DISPLAY_SLUGS) {
      if (seen.has(slug)) continue;
      ctx.addIssue({
        code: 'custom',
        message: `missing task_slug: ${slug}`,
        // 契约要求 details 指明缺哪个 slug；放进 params 让 route 能取到结构化值，
        // 而不是让调用方去正则解析 message
        params: { missing_slug: slug },
      });
    }
  })
  // superRefine 通过后再过一次元素 schema，拿到**精确类型**（上面那层入参是 unknown[]）。
  // 有 issue 时 pipe 不会执行，故不会覆盖上面收集的那批。
  .pipe(z.array(TaskCenterTaskDisplayEntrySchema));

/**
 * 从一次 `CreateAppRequestSchema` / `UpdateAppRequestSchema` 的失败里，把
 * `task_center_task_display` 缺失的 slug 汇成响应用的 `details`。
 *
 * **为什么需要它**：契约 §1.2.1 要求「缺任一 → `VALIDATION_FAILED`（`details` **指明缺哪个 slug**）」。
 * schema 侧虽然把 slug 放进了 issue 的 `params`，但两条 apps 写路由都把 issue 压成
 * `{ field, message }` 再交给 `errors.validation` —— `params` 在那一步就被丢掉，响应里根本没有
 * 契约要的 details。（这条是 codex 抓的：我原本以为「放进 params 就算结构化」，端到端并不成立。）
 *
 * 返回 `undefined` 表示本次失败与该字段无关，调用方照常不带 details。
 */
export function taskCenterTaskDisplayValidationDetails(
  error: z.ZodError,
): { missing_task_slugs: string[] } | undefined {
  const missing = error.issues
    .map((i) => (i as { params?: { missing_slug?: unknown } }).params?.missing_slug)
    .filter((v): v is string => typeof v === 'string');
  return missing.length > 0 ? { missing_task_slugs: missing } : undefined;
}

export const DEFAULT_TASK_CENTER_TASK_DISPLAY: readonly TaskCenterTaskDisplayEntry[] =
  TASK_CENTER_DISPLAY_SLUGS.map((task_slug) => ({ task_slug, is_enabled: true }));

/**
 * 小时阅读卡免费体验卡时长（秒，ADM-APP-S014 / SRV-READ-022）。
 * 契约 AB-运营应用 §3 / §4：缺省 1800、须 `> 0`（DB CHECK apps_reading_pass_free_trial_duration_positive 兜底）。
 * 前端同用本 schema 做提交前校验，避免运营撞 DB CHECK 拿到 500 而非可读的 422。
 */
/**
 * DB 列是 `integer`（int4）。只判 `> 0` 的话，超过 int4 上限的值在 Zod 这层合法、到 DB 才炸
 * ——运营拿到的是 500 而不是 422，且错误里看不出是哪一项越界。上界与 DB 列宽同源。
 */
const INT4_MAX = 2_147_483_647;
const ReadingPassFreeTrialDurationSecondsSchema = z
  .number()
  .int('Must be an integer')
  .positive('reading_pass_free_trial_duration_seconds must be > 0')
  .max(INT4_MAX, 'reading_pass_free_trial_duration_seconds exceeds integer column range');
/**
 * 小时阅读卡开启天数 = 功能解锁所需的注册自然日龄，注册日 D0（ADM-APP-S014 / SRV-READ-022）。
 * 契约 AB-运营应用 §3 / §4：缺省 2、须 `>= 1`（DB CHECK apps_reading_pass_unlock_threshold_positive 兜底）。
 */
export const ReadingPassUnlockReadingDayThresholdSchema = z
  .number()
  .int('Must be an integer')
  .min(1, 'reading_pass_unlock_reading_day_threshold must be >= 1')
  .max(INT4_MAX, 'reading_pass_unlock_reading_day_threshold exceeds integer column range');

/** App 默认与语言独立配置共用提醒边界，钟点按用户 day_cut_timezone 解释。 */
export const ReadingPassFreeTrialRemindTimeSchema = z
  .string()
  .length(5, '提醒时间须为 HH:MM')
  .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/, '提醒时间须为 HH:MM')
  .refine((value) => value >= '07:00' && value <= '21:00', '提醒时间须在 07:00–21:00');
export const ReadingPassFreeTrialRemindMaxCountSchema = z.number().int().min(1).max(10);

/**
 * S3 导入侧预期环境的闭集（`03-数据字典/70-产品配置.md` §1）。
 *
 * **只有 `Production` / `Sandbox` 两值，不含 `Unknown`**：`Unknown` 是 `environment` 列闭集的第三值、
 * 只描述导出行与 webhook 事件的取值（同目录 `60-订阅.md` §2），登记成预期值会让该 App 的全部导出行恒失配。
 * 与 DB 的 `CHECK (expected_environment IN ('Production','Sandbox'))` 同源。
 */
export const EXPECTED_ENVIRONMENTS = ['Production', 'Sandbox'] as const;
export const ExpectedEnvironmentSchema = z.enum(EXPECTED_ENVIRONMENTS);
export type ExpectedEnvironment = z.infer<typeof ExpectedEnvironmentSchema>;

/**
 * App 创建 / 编辑共享字段（ADM-APP-S001~S012 + S014 + S019）。
 * 跨字段成员校验（default_language ∈ allowed_languages）不放这里——create 用对象级 refine 强制两字段齐全时校验，
 * update 是 partial（缺省字段取库中现值合并后校验，DB 现值 service 层才可见），故 update 的完整合并校验落 service。
 */
const AppRequestFieldsSchema = z.object({
  /** 创建后不可变；产品平台显式存储，绝不从 bundle/package 推断。 */
  platform: AppPlatformSchema,
  home_screen_config: HomeScreenConfigSchema.nullable().optional(),
  slug: SlugSchema,
  name: z.string().min(1).max(128),
  // ADM-APP-S019：创建可省（省略 = null，由 service 显式写）、编辑可改含置 null。不挂 .default()：
  // 同本块其余可选字段，default 会被 .partial() 带进 PATCH，把「没传就不改」变成「没传就清空」。
  // 不进 AppPlatformConfigSchema 的 WEB 置空清单：契约允许 WEB 行填写（仅作运营标识）。
  app_code: AppCodeSchema.nullable().optional(),
  region_codes: RegionCodesSchema,
  allowed_languages: AllowedLanguagesSchema,
  // ADM-APP-S012：默认内容语言，创建必填、单选，值 ∈ allowed_languages（下方 refine + DB CHECK 兜底）。
  default_language: DefaultLanguageSchema,
  bundle_id_ios: z.string().max(255).nullable().optional(),
  package_name_android: z.string().max(255).nullable().optional(),
  adapty_app_id: z.string().max(255).nullable().optional(),
  // S3 导入侧预期环境（`03-数据字典/70-产品配置.md` §1）：列 NOT NULL，**不可空**，故不给 .nullable()。
  // 与 reading_pass 两参同理**不在这里挂 .default()**：Zod 4 的 .partial() 抵消不掉 default，
  // 漏进 PATCH 会让运营改个名就把 Sandbox 静默重置回 Production。创建侧缺省由 CreateAppRequestSchema 施加。
  expected_environment: ExpectedEnvironmentSchema,
  appsflyer_property_id: z.string().max(255).nullable().optional(),
  // ADM-APP-S009 已废弃（`00-详细需求/30-运营后台需求文档.md` 状态列 `N/A:已废弃`）：`apps` 表不承载
  // per-channel 启停列，admin 不暴露对应编辑 surface。故本请求块**不含** `is_iap_enabled` /
  // `is_stripe_enabled` / 任何 `is_<channel>_enabled` —— 创建与更新两端均 `.strict()`，
  // body 出现该类字段一律 `VALIDATION_FAILED`（`02-API契约/AB-运营应用.md` §3 / §4）。
  // 通道启停唯一入口 = A8 §7.8 `PUT /payment-method-policies` 的策略组成员集合。
  // 建 App 录入默认 35%（dev-docs #465 ADM-APP-S008：Stripe 相对 IAP 的增量 BONUS 比例默认 0.3500）
  third_party_bonus_ratio: BonusRatioSchema,
  default_chapter_price_per_kw: ChapterPricePerKwSchema,
  task_center_rewards: TaskCenterRewardsSchema,
  // ADM-APP-S016：创建可选（契约 §3 表「必填=否」）；省略时由 service 写
  // DEFAULT_TASK_CENTER_TASK_DISPLAY，**不在 schema 上挂 default**（见该 schema 的说明）。
  // PATCH 侧是整数组替换语义，未传即不改。
  task_center_task_display: TaskCenterTaskDisplaySchema.optional(),
  rewarded_ad_config: RewardedAdConfigSchema.optional(),
  // 原生必填、WEB 恒 NULL；创建及合并后的编辑状态由 AppPlatformConfigSchema 校验。
  app_link_scheme: AppLinkSchemeSchema.nullable().optional(),
  // ADM-APP-S014：小时阅读卡两个数值运营参数。
  //
  // ⚠️ 这里**故意不挂 `.default()`**：Zod 4 的 `.partial()` 不会抵消 `.default()`
  //（实测 zod 4.4.3：`UpdateAppRequestSchema.parse({ name })` 会把带 default 的键连同默认值一起产出），
  // 而 updateApp 的写入判据是 `input.X !== undefined` —— 一旦 default 漏进 PATCH，运营改个名
  // 就会把这两个参数静默重置回 1800 / 2。故创建侧的缺省改由 CreateAppRequestSchema 单独 extend 施加（见下）。
  //
  // 总开关 is_reading_pass_enabled **刻意不在这里**：不进创建入参；新建/复制由 service 显式写 false
  // （契约缺省关）。切换只走 UpdateAppRequestSchema，见下。
  reading_pass_free_trial_duration_seconds: ReadingPassFreeTrialDurationSecondsSchema,
  reading_pass_unlock_reading_day_threshold: ReadingPassUnlockReadingDayThresholdSchema,
  is_reading_pass_free_trial_remind_enabled: z.boolean(),
  reading_pass_free_trial_remind_time: ReadingPassFreeTrialRemindTimeSchema,
  reading_pass_free_trial_remind_max_count: ReadingPassFreeTrialRemindMaxCountSchema,
  // ADM-APP-S015：解锁天数创建可选；总开关不进创建入参。
  novel_purchase_unlock_days: z.number().int().min(0).optional(),
});

/**
 * POST /apps — 创建 App（ADM-APP-S001 + S002）。
 * 对象级 refine：default_language 必须 ∈ 本请求 allowed_languages（两字段创建时均必填，可一并校验）。
 */
export const CreateAppRequestSchema = AppRequestFieldsSchema.extend({
  // ADM-APP-S014（契约 §3）：两个数值参数创建可选，缺省 1800 / 2。
  // default 只施加在**创建**侧 —— 挂进 AppRequestFieldsSchema 会被 .partial() 一路带进 PATCH，
  // 把「缺省字段不改」变成「缺省字段重置」（见该 schema 处的说明）。
  reading_pass_free_trial_duration_seconds: ReadingPassFreeTrialDurationSecondsSchema.default(1800),
  reading_pass_unlock_reading_day_threshold: ReadingPassUnlockReadingDayThresholdSchema.default(2),
  // 新 Web 先于 Worker 发布，省略的提醒参数不能被前端 parse 注入旧 strict API；默认由 service 写。
  is_reading_pass_free_trial_remind_enabled: z.boolean().optional(),
  reading_pass_free_trial_remind_time: ReadingPassFreeTrialRemindTimeSchema.optional(),
  reading_pass_free_trial_remind_max_count: ReadingPassFreeTrialRemindMaxCountSchema.optional(),
  novel_purchase_unlock_days: z.number().int().min(0).default(7),
  // ADM-APP-S008：同样只在创建侧施加缺省（原先挂在共享块上，会被 .partial() 漏进 PATCH）。
  third_party_bonus_ratio: BonusRatioSchema.default('0.3500'),
  default_chapter_price_per_kw: ChapterPricePerKwSchema.default('30'),
  // 契约 §1「列 NOT NULL + 默认 Production」：创建可省。
  //
  // **`.optional()` 而不是 `.default()`**（同 task_center_task_display 的体例，缺省由 service 补）：
  // 部署顺序是 web 先、server 后（`/deploy prod`），而 `NewAppWizard` 提交的是
  // `CreateAppRequestSchema.safeParse(...).data` —— 挂 `.default()` 的话，表单没这个字段、
  // parse 却会把它塞进请求体，于是在新 web 已上线、旧 Worker 未更新的窗口里，旧的 `.strict()`
  // create schema 不认识这个键，**每一次建 App 都 VALIDATION_FAILED**（S009 那两列踩过同一个机制）。
  expected_environment: ExpectedEnvironmentSchema.optional(),
})
  // ADM-APP-S009 废弃后契约 §3 明写「本端点**也不接收**该类字段」、§反模式写「一律 VALIDATION_FAILED」——
  // 只把字段从 schema 删掉的话 Zod 会**静默 strip**：运营在旧前端上照旧提交、照旧拿 200，
  // 而这次提交里的通道意图被无声丢弃、无从察觉。`.strict()` 才是「不接收」的实现，
  // 与 UpdateAppRequestSchema 同口径（两端不一致本身就是坑）。
  .strict()
  .refine((v) => v.allowed_languages.includes(v.default_language), {
    path: ['default_language'],
    message: 'default_language must be one of allowed_languages',
  })
  .superRefine((value, ctx) => {
    const parsed = AppPlatformConfigSchema.safeParse(value);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        ctx.addIssue({ code: 'custom', path: issue.path, message: issue.message });
      }
    }
  });
export type CreateAppRequest = z.infer<typeof CreateAppRequestSchema>;

/**
 * 审核版本号形态：非空、无前后空白。**服务端不校验版本号语义** —— 不解析、不比大小、
 * 不要求匹配任何已发布版本；运营填错即该 App 无请求命中，是配置事故不是校验失败
 * （契约 `02-API契约/AB-运营应用.md` §4）。
 */
export const StoreReviewVersionSchema = z
  .string()
  .regex(/^\S(.*\S)?$/, 'store_review_version must be non-empty without surrounding whitespace');

/**
 * PATCH /apps/:id — slug 创建后不允许改（业务约束）；region_codes / allowed_languages / default_language 可改。
 * superRefine 仅在两字段【同请求都提交】时校验成员关系；单独提交任一字段时须合并库中现值校验 → 落 service
 * （dev-docs `02-API契约/AB-运营应用.md` §4：校验对象 = 合并本行现值后的目标状态）。
 */
export const UpdateAppRequestSchema = AppRequestFieldsSchema.partial()
  .omit({ slug: true, platform: true })
  // ADM-APP-S014：总开关只在编辑面出现（契约 §4「本端点是唯一切换入口」；§3 明写不进创建入参）。
  // 故不放进 AppRequestFieldsSchema（那会同时漏进 POST /apps），只在 update 侧 extend。
  .extend({
    is_reading_pass_enabled: z.boolean().optional(),
    is_novel_purchase_enabled: z.boolean().optional(),
    novel_purchase_products: z.array(NovelPurchaseProductMappingSchema).optional(),
    // ADM-APP-S017 审核版本号（契约 §4「本端点是唯一写入路径」；建 App §3 / 复制 App §5 恒 NULL，
    // 故同 ADM-APP-S014 只在 update 侧 extend）。形态 = 非空且无前后空白的串，或 `null`（= 全量关闭）。
    // **空串不是关闭的同义写法**：契约要求空串与纯空白串一律 `VALIDATION_FAILED` 且 errors[] 定位到本字段
    // —— 逐字节等值判定下空串落库会让所有带空 `X-App-Version` 的请求命中审核版本模式。
    // 形态判据与 C 端 CHECK `apps_store_review_version_shape` 同源（srv 0201）。
    store_review_version: StoreReviewVersionSchema.nullable().optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (
      v.default_language !== undefined &&
      v.allowed_languages !== undefined &&
      !v.allowed_languages.includes(v.default_language)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['default_language'],
        message: 'default_language must be one of allowed_languages',
      });
    }
  });
export type UpdateAppRequest = z.infer<typeof UpdateAppRequestSchema>;

export const CopyAppRequestSchema = z
  .object({
    slug: SlugSchema,
    name: z.string().min(1).max(128).optional(),
    // 平台从源 App 读取，原生必填与 WEB 恒 NULL 的校验在复制服务中执行。
    app_link_scheme: AppLinkSchemeSchema.nullable().optional(),
  })
  .strict();
export type CopyAppRequest = z.infer<typeof CopyAppRequestSchema>;

/** App 响应 — 以 apps 为权威字段，附带读取时派生统计。 */
export type AppResponse = {
  id: string;
  platform: AppPlatform;
  slug: string;
  name: string;
  /**
   * 包体代号（ADM-APP-S019）；未配置为 `null`。列表 / 详情均返回，复制 App 恒 `null`。
   * 字段映射：DB `apps.app_code`（varchar 12, nullable）→ 本字段原样透出。
   */
  app_code: string | null;
  /** App 运营市场集合（ISO 3166-1 alpha-2 大写），multi-select；替代旧 country 单值字段 */
  region_codes: string[];
  /** App 允许语言集合（ISO 639-1），替代旧 locale / market_timezone 单值字段 */
  allowed_languages: string[];
  /** App 默认内容语言（ISO 639-1，单选，∈ allowed_languages；ADM-APP-S012，客户端内容语言基线回落目标）*/
  default_language: string;
  bundle_id_ios: string | null;
  package_name_android: string | null;
  adapty_app_id: string | null;
  /**
   * 该 App S3 导入侧的预期环境（`03-数据字典/70-产品配置.md` §1）。
   * 字段映射：DB `apps.expected_environment`（varchar 16, NOT NULL, DEFAULT 'Production'）→ 本字段原样透出。
   * **C 端不读不下发**，只在 admin 运维登记面出现；S3 importer 与 raw 校验按它判 `ENVIRONMENT_MISMATCH`。
   */
  expected_environment: ExpectedEnvironment;
  appsflyer_property_id: string | null;
  // ADM-APP-S009 已废弃：响应**不含** is_iap_enabled / is_stripe_enabled 或任何 is_<channel>_enabled。
  // 通道启停真值 = A8 §7.8 通道策略组的成员集合（全局组 = 该 App 的全局启停，Region 覆盖组整套覆盖该 Region）。
  third_party_bonus_ratio: string;
  default_chapter_price_per_kw: string;
  task_center_rewards: TaskCenterRewards | Record<string, unknown>;
  task_center_rewards_version: number;
  /** DB apps.rewarded_ad_config 完整对象，默认值由创建服务明确施加。 */
  rewarded_ad_config: RewardedAdConfig;
  /**
   * 任务中心普通任务的开放与排序（ADM-APP-S016）。
   *
   * **类型是 `unknown`、读侧原样透出**：列是宽 jsonb，手工 SQL 改坏的历史行也要能读出来，
   * 既不能让一行坏数据把 App 详情打成 500，**也不能在读侧偷偷替换成合法默认值** ——
   * 前端正是靠「这份值解析得出来吗」判断要不要启用坏数据守卫（坏数据时不随无关保存写回，
   * 否则会静默重开 C 端已 fail-closed 的任务）。读侧一旦回落默认值，那道守卫就永远不触发。
   * 前端消费前过 `parseTaskCenterTaskDisplay` 归一（同 task_center_rewards 的处理口径）。
   *
   * **可选而非必填**：本仓的发布顺序是 Web 先于 Server（`.claude/commands/deploy.md`），
   * 那个窗口内旧 Worker **不返本字段**。建模成必填与事实不符，而前端正是靠
   * `'task_center_task_display' in dto` 判断本次 DTO 支不支持它 —— 支持才把该键放进 PATCH，
   * 否则旧 Worker 的 `.strict()` schema 会因未知键判整次保存 `VALIDATION_FAILED`
   * （与 ADM-APP-S014 三参踩过的是同一个坑）。
   */
  task_center_task_display?: unknown;
  published_at: string | null;
  is_active: boolean;
  /** 当前实际可见的去重书组数；停用 App 恒为 0。 */
  book_count: number;
  /** ADM-APP-S011 原生 deep link scheme；WEB 恒 NULL*/
  app_link_scheme: string | null;
  /** DB apps.home_screen_config 原样下发；旧 Worker 发布窗口可能缺字段。 */
  home_screen_config?: HomeScreenConfig | null;
  /**
   * 小时阅读卡 App 默认功能开关（ADM-APP-S014 / SRV-READ-019）。创建/复制 false（契约缺省关）；
   * 语言独立配置整份覆盖本值。PATCH 修改默认值，false 只挡每日新判定，不改已有终态。
   */
  is_reading_pass_enabled: boolean;
  /**
   * 60 §1：本次可用通道含 IAP 时才展示 third_party_bonus_ratio 徽章。
   *
   * ⚠️ **死字段：自引入起就从未被服务端产出过**（不是随 ADM-APP-S009 才失去生产者）——
   * **没有任何分支的当前树含 producer**：`git grep -c third_party_bonus_displayable <ref> -- packages/server`
   * 逐条扫过全部 heads / remotes（含 `origin/main` 与 `feat/sync-catchup-backend`）零命中。
   * 全仓提到它的只有三处：本类型声明、`apps.test.ts` 的一处 fixture，以及 `.dev-docs-sync.md` 那条
   * 2026-08-18 catch-up 台账（已订正为「未落地」）。历史上唯一一次实现是未合并的孤立 commit
   * `96e0e02c`（`apps-admin-service.ts` 1 处），只从本地分支 `feat/sync-catchup-backend` 可达、
   * 不在 `origin/main` 的祖先里，且该分支后续的一次 merge 已把它丢弃 —— 所以「producer 在某个待合并
   * 分支上等着」是错的，接这条缺口要从零写。
   * 要真做展示，得按 A8 §7.8 通道策略组的成员集合派生（「本次可用通道」的现行唯一真值源），
   * 而不是任何 App 级布尔列。
   */
  third_party_bonus_displayable?: boolean;
  /** 免费体验卡时长（秒，ADM-APP-S014 / SRV-READ-022）；改后只影响新领取的卡，已入账卡按自身 duration_seconds 激活 */
  reading_pass_free_trial_duration_seconds: number;
  /** 开启天数 = 功能解锁所需的注册自然日龄，注册日 D0（ADM-APP-S014 / SRV-READ-022）；改后只影响尚未写入可见性终态的用户 */
  reading_pass_unlock_reading_day_threshold: number;
  /** 免费体验卡提醒默认配置；次数为同一用户同一 App 累计逻辑提醒上限。 */
  is_reading_pass_free_trial_remind_enabled: boolean;
  reading_pass_free_trial_remind_time: string;
  reading_pass_free_trial_remind_max_count: number;
  /** 书籍买断 App 级总开关（ADM-APP-S015）；创建/复制缺省关 */
  is_novel_purchase_enabled: boolean;
  /** 买断功能解锁所需首次付费后天数 N（ADM-APP-S015） */
  novel_purchase_unlock_days: number;
  /** 价格档 → 买断产品映射（ADM-APP-S015）；未配则为 [] */
  novel_purchase_products: NovelPurchaseProductMapping[];
  /**
   * 审核版本号（ADM-APP-S017 / SRV-OPS-008）。`null` = 该 App 的审核版本模式关闭。
   * 置位期间**命中该版本的全部真实用户同等放行**，不区分审核员与普通用户。
   */
  store_review_version: string | null;
  created_at: string;
  updated_at: string;
};

export type AppPaymentMethodResponse = {
  id: string;
  app_id: string;
  /** 结算通道：取值域 = `03-数据字典/70-产品配置.md` §3 两层闭集的 `SETTLEMENT_CHANNELS` 层 */
  payment_method: SettlementChannel;
  scenario: string;
  /** `null` = 该渠道在该 scenario 未配置，不参与 serving 候选（A8 §7.7）。 */
  adapty_placement_id: string | null;
  display_capabilities: unknown[];
  /** 同 scenario 下多通道 UI 排序（升序，0 最前）—— dev-docs PR#65 sort_order→display_order */
  display_order: number;
};
